/**
 * 设备管理页面
 * v8.0 新增：IoT 设备管理功能
 */
import { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  ArrowLeft,
  Plus,
  Search,
  RefreshCw,
  Loader2,
  Smartphone,
  Ban,
  CheckCircle,
  Clock,
  Filter,
  MoreHorizontal,
  Trash2,
  Key,
  Eye,
  Edit,
  Wrench,
} from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Badge } from "@/shared/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table"
import { DeviceApiService, downloadDeviceExcel } from "@/modules/agent/services/device-api"
import { useToast } from "@/shared/hooks/useToast"
import { ToastContainer } from "@/shared/components/ui/toast"
import type { 
  Device, 
  DeviceStatus, 
  DeviceListParams,
  DeviceStats,
} from "@/modules/agent/types/device"
import { DEVICE_STATUS_CONFIG, getPermissionLabel } from "@/modules/agent/types/device"
import { DeviceCreateDialog } from "@/modules/agent/components/device/DeviceCreateDialog"
import { DeviceDetailDialog } from "@/modules/agent/components/device/DeviceDetailDialog"
import { DeviceTestPanel } from "@/modules/agent/components/device/DeviceTestPanel"
import {
  Sheet,
  SheetContent,
} from "@/shared/components/ui/sheet"

/**
 * 设备管理页面
 */
export default function DeviceManagement() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const { toasts, showError, showSuccess, removeToast } = useToast()

  // 设备列表状态
  const [devices, setDevices] = useState<Device[]>([])
  const [stats, setStats] = useState<DeviceStats>({ total: 0, active: 0, unused: 0, blocked: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)

  // 筛选状态
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<DeviceStatus | "all">("all")
  const [orderNoFilter, setOrderNoFilter] = useState("")

  // 对话框状态
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)
  const [detailMode, setDetailMode] = useState<'view' | 'edit'>('view')

  // 删除确认对话框
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null)

  // 操作确认对话框
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false)
  const [actionType, setActionType] = useState<'block' | 'unblock' | 'resetSecret' | null>(null)
  const [deviceToAction, setDeviceToAction] = useState<Device | null>(null)

  // 批量操作对话框
  const [isBatchBlockDialogOpen, setIsBatchBlockDialogOpen] = useState(false)
  const [isBatchDeleteDialogOpen, setIsBatchDeleteDialogOpen] = useState(false)
  const [batchOrderNo, setBatchOrderNo] = useState("")
  const [isBatchOperating, setIsBatchOperating] = useState(false)

  // v8.2: 测试对话框
  const [testDevice, setTestDevice] = useState<Device | null>(null)

  /**
   * 加载设备列表
   */
  const loadDevices = useCallback(async () => {
    if (!projectId) return

    setIsLoading(true)
    try {
      const params: DeviceListParams = {
        page,
        size: pageSize,
      }
      if (statusFilter !== "all") {
        params.status = statusFilter
      }
      if (orderNoFilter) {
        params.order_no = orderNoFilter
      }

      const response = await DeviceApiService.list(projectId, params)
      setDevices(response.data.devices)
      setTotal(response.data.total)
      setStats(response.data.stats)
    } catch (error) {
      console.error("加载设备列表失败:", error)
      showError("加载设备列表失败")
    } finally {
      setIsLoading(false)
    }
  }, [projectId, page, pageSize, statusFilter, orderNoFilter, showError])

  useEffect(() => {
    loadDevices()
  }, [loadDevices])

  /**
   * 过滤后的设备列表（本地搜索）
   */
  const filteredDevices = devices.filter(device => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        device.device_id.toLowerCase().includes(query) ||
        device.order_no.toLowerCase().includes(query) ||
        device.batch_no.toLowerCase().includes(query) ||
        device.name?.toLowerCase().includes(query)
      )
    }
    return true
  })

  /**
   * 处理批量生成成功
   */
  const handleCreateSuccess = async (batchNo: string) => {
    showSuccess(`批量生成成功，批次号: ${batchNo}`)
    setIsCreateDialogOpen(false)
    loadDevices()
    
    // 自动下载 Excel
    try {
      await downloadDeviceExcel(batchNo)
      showSuccess("Excel 文件已下载")
    } catch (error) {
      console.error("下载 Excel 失败:", error)
      showError("下载 Excel 失败，请手动下载")
    }
  }

  /**
   * 处理查看设备详情
   */
  const handleViewDevice = (device: Device) => {
    setSelectedDevice(device)
    setDetailMode('view')
    setIsDetailDialogOpen(true)
  }

  /**
   * 处理编辑设备
   */
  const handleEditDevice = (device: Device) => {
    setSelectedDevice(device)
    setDetailMode('edit')
    setIsDetailDialogOpen(true)
  }

  /**
   * 处理删除设备
   */
  const handleDeleteDevice = (device: Device) => {
    setDeviceToDelete(device)
    setIsDeleteDialogOpen(true)
  }

  /**
   * 确认删除设备
   */
  const confirmDelete = async () => {
    if (!projectId || !deviceToDelete) return

    try {
      await DeviceApiService.delete(projectId, deviceToDelete.device_id)
      showSuccess("设备已删除")
      setIsDeleteDialogOpen(false)
      setDeviceToDelete(null)
      loadDevices()
    } catch (error) {
      console.error("删除设备失败:", error)
      showError("删除设备失败")
    }
  }

  /**
   * 处理设备操作（吊销/解除/重置密钥）
   */
  const handleDeviceAction = (device: Device, action: 'block' | 'unblock' | 'resetSecret') => {
    setDeviceToAction(device)
    setActionType(action)
    setIsActionDialogOpen(true)
  }

  /**
   * 确认设备操作
   */
  const confirmAction = async () => {
    if (!projectId || !deviceToAction || !actionType) return

    try {
      switch (actionType) {
        case 'block':
          await DeviceApiService.block(projectId, deviceToAction.device_id)
          showSuccess("设备已吊销")
          break
        case 'unblock':
          await DeviceApiService.unblock(projectId, deviceToAction.device_id)
          showSuccess("设备已解除吊销")
          break
        case 'resetSecret':
          const result = await DeviceApiService.resetSecret(projectId, deviceToAction.device_id)
          showSuccess(`密钥已重置: ${result.data.device_secret}`)
          break
      }
      setIsActionDialogOpen(false)
      setDeviceToAction(null)
      setActionType(null)
      loadDevices()
    } catch (error) {
      console.error("操作失败:", error)
      showError("操作失败")
    }
  }

  /**
   * 处理批量吊销
   */
  const handleBatchBlock = async () => {
    if (!projectId || !batchOrderNo.trim()) {
      showError("请输入订单号")
      return
    }

    setIsBatchOperating(true)
    try {
      const result = await DeviceApiService.batchBlock(projectId, {
        order_no: batchOrderNo.trim()
      })
      showSuccess(`批量吊销成功：${result.data.affected_count} 个设备`)
      setIsBatchBlockDialogOpen(false)
      setBatchOrderNo("")
      loadDevices()
    } catch (error) {
      console.error("批量吊销失败:", error)
      showError("批量吊销失败")
    } finally {
      setIsBatchOperating(false)
    }
  }

  /**
   * 处理批量删除
   */
  const handleBatchDelete = async () => {
    if (!projectId || !batchOrderNo.trim()) {
      showError("请输入订单号")
      return
    }

    setIsBatchOperating(true)
    try {
      const result = await DeviceApiService.batchDelete(projectId, {
        order_no: batchOrderNo.trim()
      })
      showSuccess(`批量删除成功：${result.data.affected_count} 个设备`)
      setIsBatchDeleteDialogOpen(false)
      setBatchOrderNo("")
      loadDevices()
    } catch (error) {
      console.error("批量删除失败:", error)
      showError("批量删除失败")
    } finally {
      setIsBatchOperating(false)
    }
  }

  /**
   * 获取操作对话框标题和描述
   */
  const getActionDialogContent = () => {
    switch (actionType) {
      case 'block':
        return {
          title: '确认吊销设备',
          description: `确定要吊销设备 ${deviceToAction?.device_id} 吗？吊销后设备将无法连接服务。`,
        }
      case 'unblock':
        return {
          title: '确认解除吊销',
          description: `确定要解除设备 ${deviceToAction?.device_id} 的吊销状态吗？`,
        }
      case 'resetSecret':
        return {
          title: '确认重置密钥',
          description: `确定要重置设备 ${deviceToAction?.device_id} 的密钥吗？重置后旧密钥将失效。`,
        }
      default:
        return { title: '', description: '' }
    }
  }

  /**
   * 格式化日期
   */
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('zh-CN')
  }

  /**
   * 渲染状态徽章
   */
  const renderStatusBadge = (status: DeviceStatus) => {
    const config = DEVICE_STATUS_CONFIG[status]
    return (
      <Badge 
        variant={status === 'active' ? 'default' : status === 'blocked' ? 'destructive' : 'secondary'}
        className="gap-1"
      >
        <span>{config.icon}</span>
        {config.label}
      </Badge>
    )
  }

  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* 顶部导航 */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回
          </Button>
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              设备管理
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              管理项目的 IoT 设备，包括批量生成、查看、吊销等操作
            </p>
          </div>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          批量生成
        </Button>
      </div>

      {/* 统计卡片 */}
      <div className="px-6 py-4 grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">总设备数</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600 flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              正常
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-600 flex items-center gap-1">
              <Clock className="h-4 w-4" />
              未激活
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.unused}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-600 flex items-center gap-1">
              <Ban className="h-4 w-4" />
              已吊销
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.blocked}</div>
          </CardContent>
        </Card>
      </div>

      {/* 筛选栏 */}
      <div className="px-6 py-3 bg-white border-y flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="搜索设备 ID、订单号、批次号..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as DeviceStatus | "all")}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="状态" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部状态</SelectItem>
              <SelectItem value="active">正常</SelectItem>
              <SelectItem value="unused">未激活</SelectItem>
              <SelectItem value="blocked">已吊销</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="订单号"
            className="w-[160px]"
            value={orderNoFilter}
            onChange={(e) => setOrderNoFilter(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsBatchBlockDialogOpen(true)}
          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
        >
          <Ban className="h-4 w-4 mr-2" />
          批量吊销
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsBatchDeleteDialogOpen(true)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          批量删除
        </Button>
        <Button variant="outline" size="sm" onClick={loadDevices}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          刷新
        </Button>
      </div>

      {/* 设备列表 */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>设备 ID</TableHead>
                <TableHead>名称</TableHead>
                <TableHead>状态</TableHead>
                <TableHead>订单号</TableHead>
                <TableHead>批次号</TableHead>
                <TableHead>权限</TableHead>
                <TableHead>有效期至</TableHead>
                <TableHead>创建时间</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    <p className="text-sm text-slate-500 mt-2">加载中...</p>
                  </TableCell>
                </TableRow>
              ) : filteredDevices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <Smartphone className="h-12 w-12 mx-auto text-slate-300" />
                    <p className="text-sm text-slate-500 mt-2">暂无设备</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => setIsCreateDialogOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      批量生成设备
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDevices.map((device) => (
                  <TableRow key={device.device_id}>
                    <TableCell className="font-mono text-sm">{device.device_id}</TableCell>
                    <TableCell>{device.name || '-'}</TableCell>
                    <TableCell>{renderStatusBadge(device.status)}</TableCell>
                    <TableCell className="font-mono text-xs">{device.order_no}</TableCell>
                    <TableCell className="font-mono text-xs">{device.batch_no}</TableCell>
                    <TableCell>{getPermissionLabel(device.permissions)}</TableCell>
                    <TableCell className="text-sm">{formatDate(device.token_expires_at)}</TableCell>
                    <TableCell className="text-sm">{formatDate(device.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewDevice(device)}>
                            <Eye className="h-4 w-4 mr-2" />
                            查看详情
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditDevice(device)}>
                            <Edit className="h-4 w-4 mr-2" />
                            编辑
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTestDevice(device)}>
                            <Wrench className="h-4 w-4 mr-2" />
                            可用性测试
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {device.status === 'blocked' ? (
                            <DropdownMenuItem onClick={() => handleDeviceAction(device, 'unblock')}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              解除吊销
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleDeviceAction(device, 'block')}>
                              <Ban className="h-4 w-4 mr-2" />
                              吊销
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDeviceAction(device, 'resetSecret')}>
                            <Key className="h-4 w-4 mr-2" />
                            重置密钥
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteDevice(device)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            删除
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* 分页 */}
        {total > pageSize && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-500">
              共 {total} 条，第 {page} / {Math.ceil(total / pageSize)} 页
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                上一页
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= Math.ceil(total / pageSize)}
                onClick={() => setPage(p => p + 1)}
              >
                下一页
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 批量生成对话框 */}
      {projectId && (
        <DeviceCreateDialog
          projectId={projectId}
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* 设备详情对话框 */}
      {projectId && selectedDevice && (
        <DeviceDetailDialog
          projectId={projectId}
          device={selectedDevice}
          mode={detailMode}
          open={isDetailDialogOpen}
          onOpenChange={setIsDetailDialogOpen}
          onSuccess={() => {
            loadDevices()
            setIsDetailDialogOpen(false)
          }}
        />
      )}

      {/* 删除确认对话框 */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除设备</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除设备 {deviceToDelete?.device_id} 吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 操作确认对话框 */}
      <AlertDialog open={isActionDialogOpen} onOpenChange={setIsActionDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{getActionDialogContent().title}</AlertDialogTitle>
            <AlertDialogDescription>
              {getActionDialogContent().description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAction}>
              确认
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 批量吊销对话框 */}
      <AlertDialog open={isBatchBlockDialogOpen} onOpenChange={setIsBatchBlockDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>批量吊销设备</AlertDialogTitle>
            <AlertDialogDescription>
              请输入订单号，将吊销该订单下的所有设备。吊销后设备将无法连接服务。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Input
              placeholder="请输入订单号"
              value={batchOrderNo}
              onChange={(e) => setBatchOrderNo(e.target.value)}
              disabled={isBatchOperating}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBatchOperating}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBatchBlock}
              disabled={isBatchOperating || !batchOrderNo.trim()}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isBatchOperating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  处理中...
                </>
              ) : (
                "确认吊销"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 批量删除对话框 */}
      <AlertDialog open={isBatchDeleteDialogOpen} onOpenChange={setIsBatchDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">批量删除设备（危险操作）</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2">
                <p className="font-semibold">⚠️ 警告：此操作不可撤销！</p>
                <p>请输入订单号，将删除该订单下的所有设备。删除后数据无法恢复。</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Input
              placeholder="请输入订单号"
              value={batchOrderNo}
              onChange={(e) => setBatchOrderNo(e.target.value)}
              disabled={isBatchOperating}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBatchOperating}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBatchDelete}
              disabled={isBatchOperating || !batchOrderNo.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {isBatchOperating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  删除中...
                </>
              ) : (
                "确认删除"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* v8.2: 测试面板（从右侧滑出，复用 TestPanel） */}
      <Sheet open={!!testDevice} onOpenChange={() => setTestDevice(null)}>
        {/* 隐藏 SheetContent 自带的关闭按钮，使用 TestPanel 自己的 */}
        <SheetContent className="w-[500px] sm:max-w-[500px] p-0 [&>button:first-child]:hidden">
          {testDevice && (
            <DeviceTestPanel
              device={testDevice}
              projectName={projectId || ''}
              onClose={() => setTestDevice(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Toast 容器 */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}
